import React, { PropsWithChildren, useCallback, useMemo, useState } from 'react'
import * as Sentry from '@sentry/react'
import { FileKind } from '@zocks-communications/client-sdk-js'
import useOnlineStatus from './hooks/useOnlineStatus'
import { isError, parseError, S3UploadError } from './common/error'
import { useMediaDevices } from './hooks/useMediaDevices'

import { db, FilePart } from './common/database'
import { Nullable } from '../../common/Nullable'
import { FileStatus } from '../../models/File'
import { useUpdateFileMutation, useUploadSessionFileRequestMutation } from '../../store/media/media'
import { useUploadFileMutation } from '../../store/media/file'
import { RoomSession } from '../../models/Room'

import { Log } from '../../common/logger'
import { useNativeMediaRecorder } from './hooks/useNativeMediaRecorder'
import { useComplianceAudio } from './hooks/useComplianceAudio'
import { BaseAppContext, AppContext, VolumeContext } from './InPersonMeetingProvider.shared'
import { useLogger } from '../../components/Logger/hooks/useLogger'

const InPersonMeetingFileProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [uploadSessionFileRequest] = useUploadSessionFileRequestMutation()
  const [uploadFile] = useUploadFileMutation()
  const [updateFile] = useUpdateFileMutation()
  const logger = useLogger()
  const getAudioChunks = useCallback(async (accountId: string, roomId: string, sessionId: string) => {
    const parts = await db.file_parts.where({ accountId, roomId, sessionId }).toArray()
    return parts.filter((p): p is FilePart => p != null).sort((a, b) => a.sort - b.sort)
  }, [])

  const deleteAudioChunks = useCallback(
    async (accountId: string, roomId: string, sessionId: string) => {
      try {
        await db.file_parts.where({ accountId, roomId, sessionId }).delete()
        await logger.info('[InPersonMeetingProvider] Audio chunks deleted', { accountId, roomId, sessionId })
      } catch (error: unknown) {
        await logger.error('[InPersonMeetingProvider] Error during deleting audio chunks', error)
        Sentry.captureException(error)
        throw error
      }
    },
    [logger],
  )

  const _uploadFile = useCallback(
    async (accountId: string, roomId: string, sessionId: string, fileName: string, blob: Blob, kind: FileKind, metadata?: string) => {
      const {
        fileUploadUrl,
        fileUploadParameters,
        fileInfo: { fileId },
      } = await uploadSessionFileRequest({
        accountId,
        roomId,
        sessionId,
        body: {
          contentType: blob.type,
          fileName,
          fileSize: blob.size,
          kind: kind,
          metadata: metadata,
        },
      }).unwrap()

      await uploadFile({
        uploadUrl: fileUploadUrl,
        uploadParameters: fileUploadParameters,
        file: new File([blob], fileName),
      }).unwrap()
      await updateFile({ accountId, fileId, body: { status: FileStatus.UPLOADED } }).unwrap()
    },
    [updateFile, uploadFile, uploadSessionFileRequest],
  )

  const uploadLogFile = useCallback(
    async (accountId: string, roomId: string, sessionId: string, fileName: string, logs: Log[]) => {
      try {
        await logger.info('[InPersonMeetingProvider] Uploading log file...', { accountId, roomId, sessionId, fileName })
        const logLines = logs.map((log) => {
          const logPayload = JSON.stringify(log.details ?? {})
          return `[${new Date(log.timestamp).toISOString()}] type=${log.level}; msg=${log.message}; payload=${logPayload}`
        })
        const mergedBlob = new Blob(
          logLines.map((line) => line + '\n'),
          { type: 'text/plain' },
        )
        await _uploadFile(accountId, roomId, sessionId, fileName, mergedBlob, FileKind.ATTACHMENT)
        await logger.info('[InPersonMeetingProvider] Log file uploaded', { accountId, roomId, sessionId, fileName, logCount: logs.length })
      } catch (error: unknown) {
        await logger.error('[InPersonMeetingProvider] Log file upload failed', error)
        Sentry.captureException(error)
        throw error
      }
    },
    [_uploadFile, logger],
  )

  const getMimeType = (blobs: Blob[]): string => {
    return blobs[0].type.replace(/^(audio\/(webm|mp4|ogg)).*/, '$1')
  }

  const uploadAudioChunks = useCallback(
    async (accountId: string, roomId: string, sessionId: string, fileName: string, sequence: number, blobs: Blob[]) => {
      try {
        await logger.info('[InPersonMeetingProvider] Uploading audio chunks', { accountId, roomId, sessionId, fileName, sequence, blobCount: blobs.length })
        const mergedBlob = new Blob(blobs, { type: getMimeType(blobs) })
        await _uploadFile(accountId, roomId, sessionId, fileName, mergedBlob, FileKind.INTERNAL_RECORDING, JSON.stringify({ chunkOrder: sequence }))
        await logger.info('[InPersonMeetingProvider] Audio chunks uploaded', { accountId, roomId, sessionId, fileName, sequence })
      } catch (error: unknown) {
        await logger.error('[InPersonMeetingProvider] Error during processing audio chunks', error)
        Sentry.captureException(error)
        const { message, status } = parseError(error)
        throw new S3UploadError(message, typeof status === 'number' ? status : undefined)
      }
    },
    [_uploadFile, logger],
  )

  const contextValue = useMemo(
    () => ({
      getAudioChunks,
      deleteAudioChunks,
      uploadAudioChunks,
      uploadLogFile,
      logger,
    }),
    [getAudioChunks, deleteAudioChunks, uploadAudioChunks, uploadLogFile, logger],
  )

  return <BaseAppContext.Provider value={contextValue}>{children}</BaseAppContext.Provider>
}

const InPersonMeetingProvider: React.FC<PropsWithChildren<{ session: RoomSession | null }>> = ({ session: existingSession = null, children }) => {
  const logger = useLogger()
  const mediaRecorder = useNativeMediaRecorder()
  const { playComplianceAudio } = useComplianceAudio()
  const [micPermissionError, setMicPermissionError] = useState<[Nullable<string>, Nullable<string>]>([null, null])
  const [audioDeviceId, setAudioDeviceId] = useState<Nullable<string>>(null)
  const [session, setSession] = useState<Nullable<RoomSession>>(existingSession)

  const handleError = useCallback((error: unknown): [Nullable<string>, Nullable<string>] => {
    const { message } = parseError(error)

    let type = 'Generic'

    if (isError(error)) {
      if (
        (error.name === 'NotAllowedError' && error.message === 'Permission denied by system') ||
        error.name === 'NotFoundError' ||
        error.name === 'NotReadableError'
      ) {
        type = 'SystemPermissionDenied'
      } else if (error.name === 'NotAllowedError') {
        type = 'UserPermissionDenied'
      }
    }
    return [type, message]
  }, [])

  const onMediaDeviceError = useCallback(
    async (error: unknown) => {
      Sentry.captureException(error)
      setMicPermissionError(handleError(error))
    },
    [handleError],
  )

  const { devices, loading: mediaDevicesLoading } = useMediaDevices({
    constraints: {
      audio: true,
      video: false,
    },
    onError: onMediaDeviceError,
  })

  const audioInputDevices = useMemo(() => {
    if (mediaDevicesLoading) {
      return null
    }
    return devices?.filter((d) => d.kind === 'audioinput') ?? null
  }, [devices, mediaDevicesLoading])

  const currentAudioDeviceId = audioDeviceId ?? audioInputDevices?.[0]?.deviceId ?? null

  const isOnline = useOnlineStatus()

  const [wakeLockStatus, setWakeLockStatus] = useState(false)
  const [wakeLock, setWakeLock] = useState<Nullable<WakeLockSentinel>>(null)

  const requestWakeLock = useCallback(async () => {
    if (navigator.wakeLock) {
      try {
        const newWakeLock = await navigator.wakeLock.request('screen')

        newWakeLock.addEventListener('release', async () => {
          await logger.info('[InPersonMeetingProvider] Wake Lock was released')
        })
        setWakeLock(newWakeLock)
        await logger.info('[InPersonMeetingProvider] Wake Lock is active')
      } catch (error: unknown) {
        Sentry.captureException(error)
        await logger.error(`[InPersonMeetingProvider] Failed to acquire wake lock: ${(error as Error).message}`)
      }
    } else {
      await logger.error('[InPersonMeetingProvider] WakeLock not supported')
    }
  }, [logger])

  const toggleWakeLock = useCallback(async () => {
    const releaseWakeLock = () => {
      wakeLock?.release()
      setWakeLock(null)
    }
    if (wakeLockStatus) {
      releaseWakeLock()
    } else {
      await requestWakeLock()
    }
    setWakeLockStatus((status) => !status)
  }, [wakeLockStatus, wakeLock, requestWakeLock])

  const changeAudioDeviceId = useCallback(
    (deviceId: string) => {
      setAudioDeviceId(deviceId)
    },
    [setAudioDeviceId],
  )

  const setCurrentSession = useCallback(
    (session: Nullable<RoomSession>) => {
      setSession(session)
    },
    [setSession],
  )

  const createAudioChunk = useCallback(
    async (filePart: FilePart) => {
      try {
        await db.file_parts.add(filePart)
        await logger.info('[InPersonMeetingProvider] Audio chunk created', { filePart })
      } catch (error: unknown) {
        await logger.error('[InPersonMeetingProvider] Error during processing audio chunk', error)
        Sentry.captureException(error)
        if (mediaRecorder) {
          await mediaRecorder.stopRecording()
        }

        throw error
      }
    },
    [mediaRecorder, logger],
  )

  const getBrowserSpec = useCallback(async () => {
    try {
      if (navigator.storage && navigator.storage.estimate) {
        const quota = await navigator.storage.estimate()
        const totalSpace = quota.quota
        const usedSpace = quota.usage
        await logger.info('[InPersonMeetingProvider] Browser storage quota fetched', { totalSpace, usedSpace })
        return {
          totalSpace,
          usedSpace,
        }
      }
      await logger.warn('[InPersonMeetingProvider] Browser storage estimate not available')
      return undefined
    } catch (error: unknown) {
      Sentry.captureException(error)
      await logger.error('[InPersonMeetingProvider] Error getting browser storage quota', String(error))
    }
  }, [logger])

  const contextValue = useMemo(
    () => ({
      isOnline,
      micPermissionError,
      wakeLockStatus,
      audioInputDevices,
      changeAudioDeviceId,
      currentAudioDeviceId,
      setCurrentSession,
      currentSession: session,
      toggleWakeLock,
      createAudioChunk,
      mediaRecorder,
      getBrowserSpec,
      mediaDevicesLoading,
      playComplianceAudio,
    }),
    [
      isOnline,
      micPermissionError,
      wakeLockStatus,
      audioInputDevices,
      currentAudioDeviceId,
      session,
      toggleWakeLock,
      changeAudioDeviceId,
      setCurrentSession,
      createAudioChunk,
      mediaRecorder,
      getBrowserSpec,
      mediaDevicesLoading,
      playComplianceAudio,
    ],
  )

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
}

const VolumeProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [volume, setVolume] = useState(0)
  return <VolumeContext.Provider value={{ volume, setVolume }}>{children}</VolumeContext.Provider>
}

export { InPersonMeetingFileProvider, InPersonMeetingProvider, VolumeProvider }
